/**
 * THE maturity heat scale. Colors come from the API's `maturity_level` option set
 * (option.color, per CIGS-v3-DATA-MODEL §1) — this module is the only place that reads them,
 * and the only place that defines a fallback, so heat is never restyled per screen.
 *
 * Unassessed (no attribute_value row) is NOT a level: it renders as its own neutral state
 * everywhere, distinct from a real 0.
 */
import type { AttrCell, AttributeDto, OptionDto } from '@/api/types';
import { isHex, pillColors } from './color';

export const CURRENT_MATURITY = 'current_maturity';
export const TARGET_MATURITY = 'target_maturity';
export const HOT_LIST = 'hot_list';

/**
 * Heat tokens, not hexes (K10). When an option row carries no color the stop falls back to
 * the design system's `--heat-N` / `--heat-N-ink` pair, so a brand profile restyles the whole
 * ramp without this module shipping a palette of its own.
 */
const HEAT_LEVELS = 6;

function heatToken(level: number): { bg: string; ink: string } {
  const step = Math.min(Math.max(Math.round(level), 0), HEAT_LEVELS - 1);
  return { bg: `var(--heat-${step})`, ink: `var(--heat-${step}-ink)` };
}

export interface HeatStop {
  level: number;
  label: string;
  bg: string;
  ink: string;
}

export interface HeatScale {
  stops: HeatStop[];
  /** null level = Unassessed */
  at: (level: number | null | undefined) => HeatStop | null;
  max: number;
}

export function buildHeatScale(attributes: AttributeDto[] | undefined): HeatScale {
  const source =
    attributes?.find((a) => a.code === CURRENT_MATURITY)?.options?.length
      ? attributes.find((a) => a.code === CURRENT_MATURITY)!.options!
      : (attributes?.find((a) => a.code === TARGET_MATURITY)?.options ?? []);
  return heatScaleFrom(source);
}

/**
 * The same scale built straight from an option set, for screens that read option rows from
 * `/api/schema` rather than through the registry's attribute bindings. One implementation, so
 * a maturity chip means the same thing on every screen.
 */
export function heatScaleFrom(source: OptionDto[]): HeatScale {
  const stops: HeatStop[] = (source.length ? source : tokenOptions())
    .map((o) => {
      const level = o.numericValue ?? Number(o.code);
      if (isHex(o.color)) {
        const c = pillColors(o.color);
        return { level, label: o.label || String(level), bg: c.bg, ink: c.fg };
      }
      const token = heatToken(level);
      return { level, label: o.label || String(level), bg: token.bg, ink: token.ink };
    })
    .filter((s) => Number.isFinite(s.level))
    .sort((a, b) => a.level - b.level);

  const byLevel = new Map(stops.map((s) => [s.level, s]));
  return {
    stops,
    max: stops.length ? stops[stops.length - 1]!.level : 5,
    at: (level) => (level === null || level === undefined ? null : (byLevel.get(Number(level)) ?? null)),
  };
}

/** Levels with no option rows yet: labelled stops that resolve to heat tokens, never hexes. */
function tokenOptions(): OptionDto[] {
  return Array.from({ length: HEAT_LEVELS }, (_, lvl) => ({
    id: `maturity-${lvl}`,
    code: String(lvl),
    label: `Level ${lvl}`,
    color: null,
    numericValue: lvl,
  }));
}

/** The numeric level of a maturity cell, or null when Unassessed. */
export function levelOf(cell: AttrCell | undefined, options?: OptionDto[]): number | null {
  if (typeof cell?.numericValue === 'number') return cell.numericValue;
  if (!cell || cell.value === null || cell.value === undefined) {
    if (cell?.optionId && options) {
      const opt = options.find((o) => o.id === cell.optionId);
      if (opt?.numericValue !== null && opt?.numericValue !== undefined) return opt.numericValue;
    }
    return null;
  }
  if (typeof cell.value === 'number') return cell.value;
  const n = Number(cell.value);
  if (Number.isFinite(n)) return n;
  // An option code ("L3"): its option's numeric value, when the options are known.
  const opt = options?.find((o) => o.code === cell.value || o.id === cell.optionId);
  return opt?.numericValue ?? null;
}

/** Gap = target − current, only when BOTH are assessed. */
export function gapOf(current: number | null, target: number | null): number | null {
  if (current === null || target === null) return null;
  return target - current;
}

export function isUnassessed(cell: AttrCell | undefined): boolean {
  return !cell || (cell.value === null && !cell.optionId && !cell.optionCodes?.length);
}
